import { FC, useEffect, useMemo, useState } from 'react';
import { getAuthToken } from '@/utils/auth';
import { BASE_URL, getTransactionById, pauseConnector, resumeConnector } from '@/shared/api';
import { useTransactionsStore } from '@/store/transactions';
import {
  Assessment,
  classifyMessage,
  formatDuration,
  assessRecovery,
} from './assessRecovery';
import QueueBusPanel, { shouldShowQueueBus } from './QueueBusPanel';
import CoverageTimeline from './CoverageTimeline';
import {
  CoverageGap,
  catchUpEstimate,
  findCoverageGaps,
  formatSpan,
  timelineRange,
} from './coverageGaps';

type ClockRewind = { at: string | null; from: string | null; to: string | null; by: string };

type PollClock = {
  found: boolean;
  windowEnd: string | null;
  chainStart: string | null;
  pageAckCount: number;
  lastPollTime: string | null;
  rewinds: ClockRewind[];
};

type ProbeSide = { name: string; ok: boolean; message: string };

type RecoveryDeskProps = {
  integrationId: string;
  config: any;
  onStatusRefresh: () => void;
  onOpenInvestigator: () => void;
  dateFilter?: { startDate: string | null; endDate: string | null };
};

function pollIntervalSeconds(config: any): number | null {
  const raw = config?.source?.settings?.endpointPollInterval ?? config?.source?.endpointPollInterval;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function timeWindowEnabled(config: any): boolean {
  return Boolean(config?.source?.settings?.timeWindow?.enabled || config?.source?.timeWindow?.enabled);
}

function isPaused(status: string | undefined): boolean {
  const value = String(status || '').trim().toLowerCase();
  return value === 'pause' || value === 'paused';
}

function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function errorText(tx: any): string {
  if (!tx) return '';
  if (typeof tx.error === 'string' && tx.error.trim()) return tx.error.trim();
  const details = Array.isArray(tx.details) ? tx.details : [];
  for (const detail of details) {
    const failed = /fail|error/i.test(String(detail?.status || detail?.type || ''));
    const content = detail?.message || detail?.error || detail?.content;
    if (!failed && !content) continue;
    if (typeof content === 'string' && /fail|error|timeout|unauthor|denied|reject/i.test(content)) return content.trim();
    if (failed && typeof content === 'string' && content.trim()) return content.trim();
  }
  return '';
}

function probeSides(body: any): ProbeSide[] {
  if (body?.results && typeof body.results === 'object') {
    return Object.entries(body.results).map(([name, value]: [string, any]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      ok: value?.success !== false,
      message: value?.message || (value?.success === false ? 'Check failed' : 'Reachable'),
    }));
  }
  return [{
    name: 'Connection',
    ok: body?.success !== false,
    message: typeof body?.message === 'string' ? body.message : (body?.success === false ? 'Check failed' : 'Reachable'),
  }];
}

function clockSentence(clock: PollClock | null, interval: number | null): string | null {
  if (!clock?.found || !clock.windowEnd) return null;
  const lagMs = Date.now() - Date.parse(clock.windowEnd);
  if (!Number.isFinite(lagMs)) return null;
  if (lagMs < 0) return 'The stored window still ends in the future, so the next poll waits.';
  const lagMin = Math.round(lagMs / 60000);
  const behind = interval ? Math.floor(lagMs / (interval * 1000)) : null;
  const span = lagMin < 1 ? 'under a minute' : lagMin < 120 ? `${lagMin} min` : `${Math.round(lagMin / 60)} h`;
  const steps = behind != null && behind > 1 ? ` That is about ${behind} poll intervals.` : '';
  return `The window ends ${span} behind now.${steps} Each poll can walk up to 96 closed intervals toward now.`;
}

function isSnapshotSource(config: any): boolean {
  return !timeWindowEnabled(config);
}

function isCurrentStateTracking(config: any): boolean {
  const entity = String(config?.nxcFeed?.entity || '').toLowerCase();
  if (entity === 'vehicle_tracking') return true;
  const url = `${config?.source?.url || ''} ${config?.source?.endpoint || ''}`.toLowerCase();
  return /vehiclecurrentstate|vehiclelocations/.test(url);
}

function historyWindow(config: any): { enabled: boolean; fromParam: string; toParam: string } {
  const window = config?.source?.settings?.timeWindow || config?.source?.timeWindow || {};
  return {
    enabled: Boolean(window.enabled),
    fromParam: String(window.fromParam || '').trim(),
    toParam: String(window.toParam || '').trim(),
  };
}

const RecoveryDesk: FC<RecoveryDeskProps> = ({
  integrationId,
  config,
  onStatusRefresh,
  onOpenInvestigator,
  dateFilter,
}) => {
  const bucket = useTransactionsStore((state) => state.transactions[integrationId]);
  const loadingRuns = useTransactionsStore((state) => state.isLoading);
  const [clock, setClock] = useState<PollClock | null>(null);
  const [clockError, setClockError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [statusOverride, setStatusOverride] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'pause' | 'resume' | 'probe' | 'rewind' | 'backfill' | null>(null);
  const [probe, setProbe] = useState<ProbeSide[] | null>(null);
  const [rewindValue, setRewindValue] = useState('');
  const [confirmRewind, setConfirmRewind] = useState(false);
  const [selectedGapId, setSelectedGapId] = useState<string | null>(null);
  const [confirmFetch, setConfirmFetch] = useState(false);

  const status = statusOverride || config?.status || 'Unknown';
  const paused = isPaused(status);
  const interval = pollIntervalSeconds(config);
  const windowOn = timeWindowEnabled(config);
  const runs = bucket?.data || [];

  const asOf = useMemo(() => {
    const end = dateFilter?.endDate ? Date.parse(dateFilter.endDate) : NaN;
    if (Number.isFinite(end) && end < Date.now() - 60_000) return end;
    return Date.now();
  }, [dateFilter?.endDate]);

  const assessment: Assessment = useMemo(
    () => assessRecovery(runs, { asOf, pollIntervalSeconds: interval }),
    [runs, asOf, interval],
  );
  const history = historyWindow(config);
  const canFetchHistory = history.enabled && !!history.fromParam && !!history.toParam;
  const range = useMemo(
    () => timelineRange({
      dateStart: dateFilter?.startDate,
      dateEnd: dateFilter?.endDate,
      lastSuccessAt: assessment.lastSuccessAt,
    }),
    [dateFilter?.startDate, dateFilter?.endDate, assessment.lastSuccessAt],
  );
  const gaps = useMemo(
    () => findCoverageGaps(runs, {
      rangeStart: range.start,
      rangeEnd: range.end,
      pollIntervalSeconds: interval,
    }),
    [runs, range.start, range.end, interval],
  );
  const selectedGap: CoverageGap | null = gaps.find((gap) => gap.id === selectedGapId) || gaps[0] || null;
  const failedKey = assessment.failedRuns.map((run) => run.id).join(',');

  useEffect(() => {
    if (!gaps.length) {
      setSelectedGapId(null);
      return;
    }
    if (!selectedGapId || !gaps.some((gap) => gap.id === selectedGapId)) {
      const longest = [...gaps].sort((a, b) => b.durationMs - a.durationMs)[0];
      setSelectedGapId(longest.id);
    }
  }, [gaps, selectedGapId]);

  useEffect(() => {
    setStatusOverride(null);
  }, [config?.status]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = getAuthToken();
      if (!token) return;
      try {
        const response = await fetch(`${BASE_URL}/api-connectors/${integrationId}/poll-clock`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body?.message || 'The poll window could not be read.');
        if (!cancelled) {
          setClock(body);
          setClockError(null);
          if (body?.windowEnd) setRewindValue(toLocalInput(body.windowEnd));
        }
      } catch (error) {
        if (!cancelled) setClockError(error instanceof Error ? error.message : 'The poll window could not be read.');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [integrationId]);

  useEffect(() => {
    let cancelled = false;
    const ids = failedKey ? failedKey.split(',') : [];
    if (!ids.length) {
      setNotes({});
      return undefined;
    }
    const token = getAuthToken();
    if (!token) return undefined;
    (async () => {
      const next: Record<string, string> = {};
      await Promise.all(ids.slice(0, 5).map(async (id) => {
        try {
          const tx = await getTransactionById(token, integrationId, id);
          next[id] = errorText(tx);
        } catch {
          next[id] = '';
        }
      }));
      if (!cancelled) setNotes(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [failedKey, integrationId]);

  const groups = useMemo(() => {
    const map = new Map<string, { count: number; sample: string }>();
    assessment.failedRuns.forEach((run) => {
      const text = notes[run.id] || '';
      const key = text ? classifyMessage(text) : run.where;
      const current = map.get(key) || { count: 0, sample: '' };
      current.count += 1;
      if (!current.sample && text) current.sample = text.slice(0, 280);
      map.set(key, current);
    });
    return [...map.entries()];
  }, [assessment.failedRuns, notes]);

  const reloadClock = async () => {
    const token = getAuthToken();
    if (!token) return;
    const response = await fetch(`${BASE_URL}/api-connectors/${integrationId}/poll-clock`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await response.json().catch(() => ({}));
    if (response.ok) {
      setClock(body);
      if (body?.windowEnd) setRewindValue(toLocalInput(body.windowEnd));
    }
  };

  const onPauseToggle = async () => {
    const token = getAuthToken();
    if (!token) return;
    setActionError(null);
    setBusy(paused ? 'resume' : 'pause');
    try {
      if (paused) {
        await resumeConnector(integrationId, token);
        setStatusOverride('Active');
      } else {
        await pauseConnector(integrationId, token);
        setStatusOverride('Pause');
      }
      onStatusRefresh();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'The runtime did not change.');
    } finally {
      setBusy(null);
    }
  };

  const onProbe = async () => {
    const token = getAuthToken();
    if (!token) return;
    setBusy('probe');
    setActionError(null);
    setProbe(null);
    try {
      const response = await fetch(`${BASE_URL}/api-connectors/validate-config`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: config?.source,
          destination: config?.destination,
          connectionCheckOnly: true,
        }),
        signal: AbortSignal.timeout(25000),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok && !body?.results && body?.success == null) {
        throw new Error(body?.message || 'The probe did not complete.');
      }
      setProbe(probeSides(body));
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'The probe did not complete.');
    } finally {
      setBusy(null);
    }
  };

  const onFetchGap = async () => {
    const token = getAuthToken();
    if (!token || !selectedGap) return;
    setBusy('backfill');
    setActionError(null);
    let pausedHere = false;
    try {
      if (!paused) {
        await pauseConnector(integrationId, token);
        pausedHere = true;
        setStatusOverride('Pause');
      }
      const response = await fetch(`${BASE_URL}/api-connectors/${integrationId}/poll-clock/backfill`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ from: selectedGap.from }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.message || 'The missed window was not aimed.');
      await resumeConnector(integrationId, token);
      setStatusOverride('Active');
      setConfirmFetch(false);
      await reloadClock();
      onStatusRefresh();
    } catch (error) {
      if (pausedHere) {
        try {
          await resumeConnector(integrationId, token);
          setStatusOverride('Active');
        } catch {
          // Leave paused so the operator can see the error and resume themselves.
        }
      }
      setActionError(error instanceof Error ? error.message : 'The missed window was not aimed.');
    } finally {
      setBusy(null);
    }
  };

  const onRewind = async () => {
    const token = getAuthToken();
    if (!token || !rewindValue) return;
    setBusy('rewind');
    setActionError(null);
    try {
      const response = await fetch(`${BASE_URL}/api-connectors/${integrationId}/poll-clock/rewind`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ windowEnd: new Date(rewindValue).toISOString() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.message || 'The window was not moved.');
      setConfirmRewind(false);
      await reloadClock();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'The window was not moved.');
    } finally {
      setBusy(null);
    }
  };

  const lagNote = clockSentence(clock, interval);
  const bannerTone = paused ? 'warn' : assessment.tone;
  const banner = paused
    ? `Paused. ${assessment.verdict}`
    : assessment.verdict;
  const maxStage = Math.max(...assessment.stages.map((stage) => stage.p95 || 0), 1);
  const targetMs = rewindValue ? new Date(rewindValue).getTime() : NaN;
  const currentMs = clock?.windowEnd ? Date.parse(clock.windowEnd) : NaN;
  const rewindHours = Number.isFinite(targetMs) && Number.isFinite(currentMs)
    ? (currentMs - targetMs) / 3_600_000
    : null;

  const setRewindHours = (hours: number) => {
    if (!clock?.windowEnd) return;
    setRewindValue(toLocalInput(new Date(Date.parse(clock.windowEnd) - hours * 3_600_000).toISOString()));
    setConfirmRewind(false);
  };

  const setRewindToLastSuccess = () => {
    if (!assessment.lastSuccessAt) return;
    setRewindValue(toLocalInput(assessment.lastSuccessAt));
    setConfirmRewind(false);
  };

  const snapshot = isSnapshotSource(config);
  const trackingNow = isCurrentStateTracking(config);
  const skipUnchanged = Boolean(config?.changeDetection?.enabled);
  const fetchEstimate = selectedGap ? catchUpEstimate(selectedGap.durationMs, interval) : null;

  return (
    <div className="studio-recovery">
      <p className={`studio-verdict is-${bannerTone}`}>
        {loadingRuns && !runs.length ? 'Reading recent runs…' : banner}
      </p>

      <div className="studio-stats">
        {assessment.stats.map((stat) => (
          <div key={stat.label} className={`studio-stat is-${stat.tone}`}>
            <span>{stat.label}</span>
            <strong>{stat.value}</strong>
          </div>
        ))}
      </div>
      {bucket?.pagination?.total && bucket.pagination.total > runs.length ? (
        <p className="studio-note">Based on the latest {runs.length} runs in this range. Widen the date range to see an older stop.</p>
      ) : (
        <p className="studio-note">Based on the runs loaded for this range. Widen the date range to see an older stop.</p>
      )}

      {shouldShowQueueBus(config, runs) ? (
        <QueueBusPanel integrationId={integrationId} rateLimited={groups.some(([label]) => label === 'Rate limit')} />
      ) : null}

      <div className="studio-split">
        <section className="studio-panel">
          <h3>What to look at</h3>
          {assessment.findings.length === 0 ? <p>Nothing in this sample stands out.</p> : null}
          {assessment.findings.map((finding) => (
            <article key={finding.id} className={`studio-finding is-${finding.tone}`}>
              <strong>{finding.title}</strong>
              <p>{finding.detail}</p>
            </article>
          ))}
          {groups.length ? (
            <div className="studio-groups">
              {groups.map(([label, group]) => (
                <article key={label}>
                  <strong>{group.count} · {label}</strong>
                  {group.sample ? <p>{group.sample}</p> : <p>Open the run in Investigator for the payload. This list did not include an error string.</p>}
                </article>
              ))}
              <button type="button" className="studio-btn studio-btn--ghost" onClick={onOpenInvestigator}>Open investigator</button>
            </div>
          ) : null}
        </section>

        <section className="studio-panel">
          <h3>Where the time went</h3>
          {assessment.stages.map((stage) => (
            <div key={stage.stage} className="studio-stage">
              <div className="studio-stage__label">
                <span>{stage.stage}</span>
                <span>
                  {stage.p95 == null
                    ? 'Not recorded'
                    : `${formatDuration(stage.median || 0)} typical · ${formatDuration(stage.p95)} tail`}
                </span>
              </div>
              <div className="studio-stage__track">
                <div
                  className={stage.slow ? 'is-slow' : ''}
                  style={{ width: `${Math.max(4, ((stage.p95 || 0) / maxStage) * 100)}%` }}
                />
              </div>
            </div>
          ))}
          <p className="studio-note">Tail is the slowest tenth of the runs that recorded that stage. Under four samples, a single outlier is not called slow.</p>
        </section>
      </div>

      <section className="studio-panel">
        <div className="studio-panel__head">
          <h3>Coverage</h3>
          {interval ? <span className="studio-badge">Every {interval}s</span> : null}
        </div>
        <p>
          {canFetchHistory
            ? 'Select a missed span and fetch it. The next polls ask the source for that range. Unchanged records are not posted again.'
            : snapshot
              ? 'This source reads the current snapshot. The timeline still shows when polls stopped. Fetching a missed span needs a history API with a time window in Settings.'
              : 'A time window is on, but the from/to query parameters are missing in Settings.'}
          {trackingNow && !canFetchHistory
            ? ' Last-known GPS cannot reconstruct pings from a gap.'
            : ''}
        </p>
        <CoverageTimeline
          rangeStart={range.start}
          rangeEnd={range.end}
          gaps={gaps}
          runs={runs}
          selectedId={selectedGap?.id || null}
          onSelect={(gap) => { setSelectedGapId(gap.id); setConfirmFetch(false); }}
          canFetch={canFetchHistory}
          pollIntervalSeconds={interval}
        />
        {selectedGap && canFetchHistory ? (
          <div className="studio-rewind">
            <button
              type="button"
              className="studio-btn"
              disabled={busy === 'backfill'}
              onClick={() => setConfirmFetch(true)}
            >
              Fetch missed window
            </button>
            {confirmFetch ? (
              <div className="studio-confirm">
                <p>
                  Fetch {new Date(selectedGap.from).toLocaleString()} → {new Date(selectedGap.to).toLocaleString()}
                  {' '}({formatSpan(selectedGap.durationMs)}).
                  The integration pauses, aims the poll window at the start of the gap, then resumes.
                  About {fetchEstimate?.polls} poll
                  {fetchEstimate?.polls === 1 ? '' : 's'}
                  {' '}({fetchEstimate ? formatDuration(fetchEstimate.wallMs) : ''}).
                  A step longer than 14 days is fetched in 14-day chunks.
                  {skipUnchanged ? ' Unchanged records are not posted again.' : ''}
                </p>
                <div>
                  <button type="button" disabled={busy === 'backfill'} onClick={onFetchGap}>
                    {busy === 'backfill' ? 'Fetching…' : 'Fetch this span'}
                  </button>
                  <button type="button" className="keep" disabled={busy === 'backfill'} onClick={() => setConfirmFetch(false)}>Keep</button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        {selectedGap && !canFetchHistory ? (
          <p className="studio-note">
            Point the source at a history endpoint, turn on Time window in Settings, set the from and to query parameters the API expects, then return here and fetch this span.
          </p>
        ) : null}
      </section>

      <section className="studio-panel">
        <div className="studio-panel__head">
          <h3>Poll window</h3>
          {interval ? <span className="studio-badge">Every {interval}s</span> : null}
        </div>
        {clockError ? <p className="studio-error">{clockError}</p> : null}
        {!windowOn && !clock?.found ? (
          <p>This source has no time window, so there is nothing to rewind. Use Coverage above to see missed spans.</p>
        ) : null}
        {windowOn && clock && !clock.found ? (
          <p>No window is stored yet. The first poll that the destination accepts writes it. A failed first poll leaves it unset and retries the same start.</p>
        ) : null}
        {clock?.found ? (
          <>
            <p>
              {clock.windowEnd ? `Stored end ${new Date(clock.windowEnd).toLocaleString()} (${clock.windowEnd}).` : 'Stored end is empty.'}
              {clock.chainStart ? ` Chain started ${clock.chainStart}.` : ''}
            </p>
            {lagNote ? <p>{lagNote}</p> : null}
            {clock.pageAckCount > 0 ? (
              <p>{clock.pageAckCount} page{clock.pageAckCount === 1 ? '' : 's'} of the current window were accepted. The window stays put until every page is accepted, so a later page failure does not skip ahead.</p>
            ) : null}
            {clock.rewinds?.[0] ? (
              <p className="studio-note">
                Last move {clock.rewinds[0].at ? new Date(clock.rewinds[0].at).toLocaleString() : ''} from {clock.rewinds[0].from} to {clock.rewinds[0].to}
                {clock.rewinds[0].by ? ` by ${clock.rewinds[0].by}` : ''}.
              </p>
            ) : null}
            <div className="studio-rewind">
              <div className="studio-chips">
                <button type="button" className="studio-chip" disabled={!assessment.lastSuccessAt} onClick={setRewindToLastSuccess}>
                  Last successful run
                </button>
                {[1, 6, 24].map((hours) => (
                  <button key={hours} type="button" className="studio-chip" disabled={!clock.windowEnd} onClick={() => setRewindHours(hours)}>
                    Back {hours === 24 ? '1 day' : `${hours}h`}
                  </button>
                ))}
              </div>
              <label>
                New window end
                <input type="datetime-local" value={rewindValue} onChange={(event) => { setRewindValue(event.target.value); setConfirmRewind(false); }} />
              </label>
              {paused ? (
                <button type="button" className="studio-btn studio-btn--ghost" disabled={!rewindValue || busy === 'rewind'} onClick={() => setConfirmRewind(true)}>
                  Review move
                </button>
              ) : (
                <p className="studio-note">Pause the integration before moving the window. A running poll can advance it at the same time.</p>
              )}
              {confirmRewind ? (
                <div className="studio-confirm">
                  <p>
                    Move the window back{rewindHours != null && rewindHours > 0 ? ` about ${rewindHours.toFixed(1)} hours` : ''}.
                    The next polls re-read that span. Unchanged records are not posted again. A record that was accepted before its change hash was stored can be sent a second time.
                    Move the window when a span was missed. A destination that is rejecting records will reject them again until that side is fixed.
                  </p>
                  <div>
                    <button type="button" disabled={busy === 'rewind'} onClick={onRewind}>{busy === 'rewind' ? 'Moving…' : 'Move window back'}</button>
                    <button type="button" className="keep" disabled={busy === 'rewind'} onClick={() => setConfirmRewind(false)}>Keep</button>
                  </div>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </section>

      <section className="studio-panel">
        <h3>Restore</h3>
        <div className="studio-actions">
          <button type="button" className={paused ? 'studio-btn' : 'studio-btn studio-btn--danger'} disabled={busy === 'pause' || busy === 'resume'} onClick={onPauseToggle}>
            {busy === 'pause' || busy === 'resume' ? 'Working…' : paused ? 'Resume' : 'Pause'}
          </button>
          <button type="button" className="studio-btn studio-btn--ghost" disabled={busy === 'probe'} onClick={onProbe}>
            {busy === 'probe' ? 'Probing…' : 'Probe source and destination'}
          </button>
        </div>
        <p className="studio-note">
          Pause stops new polls when the runtime actually stops. Probe checks that both sides accept a connection now, without writing a record.
          Payload replay on the Replay tab does not republish a run, so it will not restore a missed span.
        </p>
        {probe ? (
          <div className="studio-probe">
            {probe.map((side) => (
              <article key={side.name} className={side.ok ? 'is-ok' : 'is-bad'}>
                <strong>{side.name}</strong>
                <span>{side.ok ? 'Reachable' : 'Failed'}</span>
                <p>{side.message}</p>
              </article>
            ))}
          </div>
        ) : null}
        {actionError ? <p className="studio-error">{actionError}</p> : null}
      </section>
    </div>
  );
};

export default RecoveryDesk;
